import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Check, GripVertical, Loader2, Search } from "lucide-react";
import { KbSectionClausePreviewModal } from "../components/organisms/KbSectionClausePreviewModal";
import {
  type GenerateFlowField,
  type GenerateFlowInputStep,
} from "../data/generateFlowInputFields";
import {
  buildFieldSteps,
  clearGenerationSessionId,
  commitGenerationSession,
  createGenerationSession,
  getGenerationSession,
  getSessionAiStatus,
  listKbSections,
  patchGenerationSession,
  persistGenerationSessionId,
  previewGenerationSession,
  readStoredGenerationSessionId,
  resolveSessionPlaceholders,
  startSessionAi,
} from "../services/gameRuleKbGenerateService";
import {
  getKbGenerationTemplate,
  saveKbGenerationTemplate,
} from "../services/gameRuleKbGenerationTemplateService";
import { ApiError } from "../services/apiClient";
import { openDraftInHost } from "../services/hostDocumentService";
import { listUsers } from "../services/userService";
import type {
  KbSectionListItem,
  SectionAiResult,
  SectionAiSelection,
} from "../types/gameRuleKbGenerate";
import type { UserOption } from "../types/user";
import {
  allSectionsHaveAiSelection,
  anySectionNeedsAiGeneration,
  countAiGeneratedSections,
  getSectionAiGenerationState,
  isSectionAiBrowsable,
  sleep,
} from "../utils/generateFlowAi";
import {
  buildFieldValuesPayload,
  buildProgrammeFromStep1,
  filterKbSectionInputSteps,
  initializeInputFieldValuesFromSteps,
  mapApiFieldStepsToInputSteps,
  orderFieldStepsBySelection,
} from "../utils/generateFlowFieldSteps";
import {
  areAllInputSectionsValid,
  fieldValueKey,
  getCompletedSectionIndexes,
  getFieldValue,
  isInputSectionValid,
} from "../utils/generateFlowInputValidation";
import {
  getKbPageNumbers,
  KB_SECTIONS_PAGE_SIZE,
} from "../utils/kbSectionPagination";
import { hydrateGenerationSession } from "../utils/hydrateGenerationSession";
import { createPreviewDebounce } from "../utils/generateFlowDocumentPreview";
import {
  appendAiSelectionOrder,
  deriveAiSelectionOrder,
} from "../utils/generateFlowSelectionOrder";
import {
  CONTENT_MODE_AI_SUGGESTIONS,
  CONTENT_MODE_PLACEHOLDER_ONLY,
  FLOW_TYPE_FROM_TEMPLATE,
} from "../constants/kbGenerationFlow";

type GenerateWizardLocationState = {
  readonly resumeSessionId?: string;
  readonly startNew?: boolean;
  readonly fromTemplateId?: string;
};

type GenerateStep = 1 | 2 | 3 | 4 | 5;

type SectionRow = {
  readonly id: string;
  readonly name: string;
  readonly description: string;
  readonly fields: number;
  readonly tag: string;
};

const kbSectionToSectionRow = (item: KbSectionListItem): SectionRow => ({
  id: item.id,
  name: item.name,
  description: item.description ?? "",
  fields: item.fieldCount,
  tag: item.primaryTag ?? "General",
});

const stepText: Record<
  GenerateStep,
  { readonly title: string; readonly subtitle: string; readonly cta: string }
> = {
  1: {
    title: "Generate Document - Step 1 of 5",
    subtitle: "Name your game rule and continue to section selection",
    cta: "Select Sections",
  },
  2: {
    title: "Generate Document - Step 2 of 5",
    subtitle: "Select sections to include in your document",
    cta: "Fill Input Fields",
  },
  3: {
    title: "Generate Document - Step 3 of 5",
    subtitle: "Fill Input Fields - One section per step",
    cta: "Select AI Content",
  },
  4: {
    title: "Generate Document - Step 4 of 5",
    subtitle:
      "AI Content Suggestions - View, select & edit one option per section",
    cta: "Arrange & Generate",
  },
  5: {
    title: "Generate Document - Step 5 of 5",
    subtitle: "Rearrange Sections & Generate Document",
    cta: "Generate Document",
  },
};

const getStepCopy = (
  step: GenerateStep,
  options: {
    readonly fromTemplate: boolean;
    readonly placeholderOnly: boolean;
  },
): {
  readonly title: string;
  readonly subtitle: string;
  readonly cta: string;
} => {
  if (step === 1 && options.fromTemplate) {
    return {
      title: stepText[1].title,
      subtitle:
        "Name your game rule — sections are loaded from your saved template",
      cta: "Fill Input Fields",
    };
  }
  if (step === 3 && options.placeholderOnly) {
    return {
      ...stepText[3],
      cta: "Review Placeholder Content",
    };
  }
  if (step === 4 && options.placeholderOnly) {
    return {
      title: stepText[4].title,
      subtitle: "Placeholder content — review resolved text per section",
      cta: stepText[4].cta,
    };
  }
  return stepText[step];
};

const tagColor = (tag: string): string => {
  if (tag === "Legal") return "bg-sky-600";
  if (tag === "Entry") return "bg-teal-600";
  if (tag === "Game") return "bg-violet-700";
  if (tag === "Prize") return "bg-orange-500";
  if (tag === "General") return "bg-slate-700";
  if (tag === "Compliance") return "bg-red-600";
  if (tag === "Eligibility") return "bg-violet-700";
  return "bg-orange-500";
};

const defaultDocumentFileName = (ruleName: string): string => {
  const trimmed = ruleName.trim();
  if (!trimmed) {
    return "game-rule.docx";
  }
  return trimmed.toLowerCase().endsWith(".docx") ? trimmed : `${trimmed}.docx`;
};

const truncateText = (text: string, maxLength = 120): string => {
  const trimmed = text.trim();
  if (trimmed.length <= maxLength) {
    return trimmed;
  }
  return `${trimmed.slice(0, maxLength)}...`;
};

export const GameRuleGenerateFlowPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [step, setStep] = useState<GenerateStep>(1);
  const [gameRuleTitle, setGameRuleTitle] = useState("");
  const [gameRuleName, setGameRuleName] = useState("");
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [kbPage, setKbPage] = useState(1);
  const [kbSections, setKbSections] = useState<SectionRow[]>([]);
  const [sectionsTotal, setSectionsTotal] = useState(0);
  const [sectionsLoading, setSectionsLoading] = useState(false);
  const [sectionsError, setSectionsError] = useState<string | null>(null);
  const [sectionRowById, setSectionRowById] = useState<
    Record<string, SectionRow>
  >({});
  const [selectedSectionIds, setSelectedSectionIds] = useState<string[]>([]);
  const [inputSteps, setInputSteps] = useState<
    readonly GenerateFlowInputStep[]
  >([]);
  const [fieldStepsLoading, setFieldStepsLoading] = useState(false);
  const [fieldStepsError, setFieldStepsError] = useState<string | null>(null);
  const [savedSessionProgramme, setSavedSessionProgramme] = useState<Record<
    string,
    string
  > | null>(null);
  const [activeInputSection, setActiveInputSection] = useState(0);
  const [inputFieldValues, setInputFieldValues] = useState<
    Record<string, string>
  >({});
  const [activeAiSectionIndex, setActiveAiSectionIndex] = useState(0);
  const [aiSessionStatus, setAiSessionStatus] = useState<string | null>(null);
  const [aiInitialLoading, setAiInitialLoading] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  const [aiResults, setAiResults] = useState<Record<string, SectionAiResult>>(
    {},
  );
  const [aiProgress, setAiProgress] = useState<{
    total: number;
    completed: number;
    currentSectionId: string | null;
  } | null>(null);
  const [aiSelectionsBySection, setAiSelectionsBySection] = useState<
    Record<string, SectionAiSelection>
  >({});
  const [expandedOptionKey, setExpandedOptionKey] = useState<string | null>(
    null,
  );
  const [editingSectionId, setEditingSectionId] = useState<string | null>(null);
  const [clausePreview, setClausePreview] = useState<{
    readonly sectionName: string;
    readonly tag: string | null;
    readonly description: string | null;
    readonly content: string;
  } | null>(null);
  const [editDraft, setEditDraft] = useState("");
  const [orderedSections, setOrderedSections] = useState<string[]>([]);
  const [aiSelectionOrder, setAiSelectionOrder] = useState<string[]>([]);
  const [documentPreviewLoading, setDocumentPreviewLoading] = useState(false);
  const [documentPreviewError, setDocumentPreviewError] = useState<
    string | null
  >(null);
  const previewDebounceRef = useRef(createPreviewDebounce());
  const previewRequestIdRef = useRef(0);
  const persistPreviewSeqRef = useRef(0);
  const [fileName, setFileName] = useState("");
  const [reviewers, setReviewers] = useState<UserOption[]>([]);
  const [reviewersLoading, setReviewersLoading] = useState(false);
  const [reviewersError, setReviewersError] = useState<string | null>(null);
  const [formValues, setFormValues] = useState({
    sponsorName: "",
    administratorName: "",
    reviewerUserId: "",
    notes: "",
  });
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [sessionHydrating, setSessionHydrating] = useState(false);
  const resetStep3FieldsOnLoadRef = useRef(false);
  const [fromTemplateId, setFromTemplateId] = useState<string | null>(null);
  const [sourceTemplateName, setSourceTemplateName] = useState<string | null>(
    null,
  );
  const [flowFromTemplate, setFlowFromTemplate] = useState(false);
  const [contentMode, setContentMode] = useState<string>(
    CONTENT_MODE_AI_SUGGESTIONS,
  );
  const [placeholderOnlyChecked, setPlaceholderOnlyChecked] = useState(false);
  const [templateBootstrapLoading, setTemplateBootstrapLoading] =
    useState(false);
  const [saveAsTemplate, setSaveAsTemplate] = useState(false);
  const [saveTemplateName, setSaveTemplateName] = useState("");

  const isPlaceholderMode = contentMode === CONTENT_MODE_PLACEHOLDER_ONLY;

  useEffect(() => {
    const locationState = location.state as GenerateWizardLocationState | null;
    const params = new URLSearchParams(location.search);
    const resumeSessionId =
      locationState?.resumeSessionId?.trim() ||
      params.get("sessionId")?.trim() ||
      null;

    if (locationState?.startNew) {
      clearGenerationSessionId();
      return;
    }

    const pendingTemplateId = locationState?.fromTemplateId?.trim() || null;
    if (pendingTemplateId) {
      let cancelled = false;
      setTemplateBootstrapLoading(true);
      setFromTemplateId(pendingTemplateId);
      setFlowFromTemplate(true);
      setError(null);
      void (async () => {
        try {
          const template = await getKbGenerationTemplate(pendingTemplateId);
          if (cancelled) {
            return;
          }
          setSourceTemplateName(template.name);
          setSelectedSectionIds([...template.sectionIds]);
          setOrderedSections([...template.sectionIds]);
        } catch (err: unknown) {
          if (!cancelled) {
            setError(
              err instanceof ApiError
                ? err.message
                : err instanceof Error
                  ? err.message
                  : "Unable to load template. Try again from the landing page.",
            );
          }
        } finally {
          if (!cancelled) {
            setTemplateBootstrapLoading(false);
          }
        }
      })();
      return () => {
        cancelled = true;
      };
    }

    if (resumeSessionId) {
      let cancelled = false;
      setSessionHydrating(true);
      setError(null);
      void (async () => {
        try {
          const session = await getGenerationSession(resumeSessionId);
          if (cancelled) {
            return;
          }
          const hydrated = hydrateGenerationSession(session);
          persistGenerationSessionId(hydrated.sessionId);
          setSessionId(hydrated.sessionId);
          setStep(hydrated.step);
          setGameRuleTitle(hydrated.gameRuleTitle);
          setGameRuleName(hydrated.gameRuleName);
          setSelectedSectionIds([...hydrated.selectedSectionIds]);
          setOrderedSections([...hydrated.orderedSections]);
          setAiSelectionOrder([...hydrated.aiSelectionOrder]);
          setSavedSessionProgramme(hydrated.savedSessionProgramme);
          setAiResults(hydrated.aiResults);
          setAiSelectionsBySection(hydrated.aiSelectionsBySection);
          setFileName(hydrated.fileName);
          setFormValues((prev) => ({
            ...prev,
            sponsorName: hydrated.formValues.sponsorName,
            administratorName: hydrated.formValues.administratorName,
            notes: hydrated.formValues.notes,
          }));
          setContentMode(hydrated.contentMode);
          setPlaceholderOnlyChecked(
            hydrated.contentMode === CONTENT_MODE_PLACEHOLDER_ONLY,
          );
          setFlowFromTemplate(hydrated.flowType === FLOW_TYPE_FROM_TEMPLATE);
          setFromTemplateId(hydrated.templateId);
        } catch (err: unknown) {
          if (!cancelled) {
            setError(
              err instanceof ApiError
                ? err.message
                : err instanceof Error
                  ? err.message
                  : "Unable to restore your draft. Try again from Generated Documents.",
            );
          }
        } finally {
          if (!cancelled) {
            setSessionHydrating(false);
          }
        }
      })();
      return () => {
        cancelled = true;
      };
    }

    const storedSessionId = readStoredGenerationSessionId();
    if (storedSessionId) {
      setSessionId(storedSessionId);
    }
  }, [location.state, location.search]);

  useEffect(() => {
    setKbPage(1);
  }, [search]);

  const searchQuery = search.trim();

  useEffect(() => {
    if (step !== 2) {
      return;
    }

    let cancelled = false;
    setSectionsLoading(true);
    setSectionsError(null);

    void listKbSections({
      search: searchQuery || undefined,
      page: kbPage,
      limit: KB_SECTIONS_PAGE_SIZE,
    })
      .then(({ items, total }) => {
        if (cancelled) {
          return;
        }
        const rows = items.map(kbSectionToSectionRow);
        setKbSections(rows);
        setSectionsTotal(total);
        setSectionRowById((prev) => {
          const next = { ...prev };
          for (const row of rows) {
            next[row.id] = row;
          }
          return next;
        });
      })
      .catch((err: unknown) => {
        if (cancelled) {
          return;
        }
        setKbSections([]);
        setSectionsTotal(0);
        setSectionsError(
          err instanceof ApiError
            ? err.message
            : err instanceof Error
              ? err.message
              : "Unable to load Knowledgebase sections.",
        );
      })
      .finally(() => {
        if (!cancelled) {
          setSectionsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [step, searchQuery, kbPage]);

  const totalKbPages = Math.max(
    1,
    Math.ceil(sectionsTotal / KB_SECTIONS_PAGE_SIZE),
  );

  useEffect(() => {
    if (kbPage > totalKbPages) {
      setKbPage(totalKbPages);
    }
  }, [kbPage, totalKbPages]);

  const clampedKbPage = Math.min(kbPage, totalKbPages);
  const rangeStart =
    sectionsTotal === 0 ? 0 : (clampedKbPage - 1) * KB_SECTIONS_PAGE_SIZE + 1;
  const rangeEnd = Math.min(
    clampedKbPage * KB_SECTIONS_PAGE_SIZE,
    sectionsTotal,
  );
  const kbPageNumbers = getKbPageNumbers(clampedKbPage, totalKbPages);

  const fieldStepsKey = selectedSectionIds.join(",");
  const selectedSectionsKey = selectedSectionIds.join(",");

  useEffect(() => {
    if (
      (step !== 3 && step !== 4 && step !== 5) ||
      selectedSectionIds.length === 0
    ) {
      return;
    }

    let cancelled = false;
    setFieldStepsLoading(true);
    setFieldStepsError(null);

    void (async () => {
      try {
        const sessionDetail = sessionId
          ? await getGenerationSession(sessionId).catch(() => null)
          : null;
        const apiSteps = await buildFieldSteps(selectedSectionIds);
        if (cancelled) {
          return;
        }

        const orderedSteps = orderFieldStepsBySelection(
          apiSteps,
          selectedSectionIds,
        );
        const sectionRowsFromSteps = orderedSteps
          .filter((step) => step.id !== "programme")
          .map((step) => ({
            id: step.id,
            name: step.name,
            description: step.description ?? "",
            fields: step.fields.length,
            tag: step.tag ?? "General",
          }));
        const mappedSteps = mapApiFieldStepsToInputSteps(orderedSteps);
        setInputSteps(mappedSteps);
        setSectionRowById((prev) => {
          const next = { ...prev };
          for (const row of sectionRowsFromSteps) {
            const existing = next[row.id];
            if (
              !existing ||
              existing.name === existing.id ||
              existing.name.trim().length === 0
            ) {
              next[row.id] = row;
              continue;
            }
            next[row.id] = {
              ...existing,
              description: existing.description || row.description,
              fields: existing.fields || row.fields,
              tag:
                existing.tag === "General" && row.tag !== "General"
                  ? row.tag
                  : existing.tag,
            };
          }
          return next;
        });
        setSavedSessionProgramme(sessionDetail?.programme ?? null);
        const shouldResetFromApi =
          step === 3 && resetStep3FieldsOnLoadRef.current;
        if (shouldResetFromApi) {
          resetStep3FieldsOnLoadRef.current = false;
        }
        setInputFieldValues((prev) =>
          initializeInputFieldValuesFromSteps(
            mappedSteps,
            shouldResetFromApi ? {} : prev,
            {
              savedFieldValues: shouldResetFromApi
                ? null
                : (sessionDetail?.fieldValues ?? null),
              useApiDefaultsOnly: shouldResetFromApi,
            },
          ),
        );
      } catch (err: unknown) {
        if (cancelled) {
          return;
        }
        setInputSteps([]);
        setFieldStepsError(
          err instanceof ApiError
            ? err.message
            : err instanceof Error
              ? err.message
              : "Unable to load input fields.",
        );
      } finally {
        if (!cancelled) {
          setFieldStepsLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
    // Pre-fill uses Step 1/2 values at load time only; avoid refetch on programme edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fieldStepsKey + step gate reload
  }, [step, fieldStepsKey, sessionId]);

  useEffect(() => {
    if (activeAiSectionIndex >= selectedSectionIds.length) {
      setActiveAiSectionIndex(Math.max(0, selectedSectionIds.length - 1));
    }
  }, [activeAiSectionIndex, selectedSectionIds.length]);

  const resetStepFourAiState = useCallback(() => {
    setAiResults({});
    setAiSelectionsBySection({});
    setAiSessionStatus(null);
    setAiProgress(null);
    setAiError(null);
    setEditingSectionId(null);
    setExpandedOptionKey(null);
  }, []);

  useEffect(() => {
    if (step !== 4 || !sessionId) {
      return;
    }

    let cancelled = false;

    resetStepFourAiState();
    setAiInitialLoading(true);
    setAiError(null);

    const applyAiStatus = (
      status: Awaited<ReturnType<typeof getSessionAiStatus>>,
    ) => {
      setAiSessionStatus(status.status);
      setAiResults(status.aiResults);
      setAiProgress(status.progress);
      if (status.status === "ai_failed") {
        setAiError(status.error ?? "AI generation failed. Try again.");
      }
    };

    const pollWhileRunning = async () => {
      for (let attempt = 0; attempt < 150; attempt += 1) {
        if (cancelled) {
          return;
        }
        await sleep(2000);
        const status = await getSessionAiStatus(sessionId);
        if (cancelled) {
          return;
        }
        applyAiStatus(status);
        if (status.status !== "ai_running") {
          return;
        }
      }
    };

    void (async () => {
      try {
        const [session, statusInitial] = await Promise.all([
          getGenerationSession(sessionId),
          getSessionAiStatus(sessionId),
        ]);
        if (cancelled) {
          return;
        }

        applyAiStatus(statusInitial);
        let status = statusInitial;

        if (
          session.aiSelections &&
          Object.keys(session.aiSelections).length > 0
        ) {
          setAiSelectionsBySection(session.aiSelections);
          setAiSelectionOrder(
            deriveAiSelectionOrder(
              session.sectionOrder,
              session.aiSelections,
              session.selectedSectionIds,
            ),
          );
        }

        const shouldStartAi =
          !isPlaceholderMode &&
          status.status !== "ai_running" &&
          (status.status !== "ai_ready" ||
            anySectionNeedsAiGeneration(selectedSectionIds, status.aiResults));

        if (shouldStartAi) {
          await startSessionAi(sessionId);
          status = await getSessionAiStatus(sessionId);
          if (cancelled) {
            return;
          }
          applyAiStatus(status);
        }

        if (!cancelled) {
          setAiInitialLoading(false);
        }

        if (status.status === "ai_running") {
          await pollWhileRunning();
        }
      } catch (err: unknown) {
        if (!cancelled) {
          setAiError(
            err instanceof ApiError
              ? err.message
              : err instanceof Error
                ? err.message
                : "Unable to load AI suggestions.",
          );
        }
      } finally {
        if (!cancelled) {
          setAiInitialLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    step,
    sessionId,
    selectedSectionsKey,
    resetStepFourAiState,
    isPlaceholderMode,
  ]);

  useEffect(() => {
    if (step !== 5 || !sessionId) {
      return;
    }

    let cancelled = false;
    setReviewersLoading(true);
    setReviewersError(null);

    void (async () => {
      try {
        const [users, session] = await Promise.all([
          listUsers(),
          getGenerationSession(sessionId),
        ]);
        if (cancelled) {
          return;
        }
        setReviewers(users);
        if (session.fileName?.trim()) {
          setFileName(session.fileName.trim());
        } else {
          setFileName(
            (prev) => prev.trim() || defaultDocumentFileName(gameRuleName),
          );
        }
        if (session.reviewerNotes?.trim()) {
          setFormValues((prev) => ({
            ...prev,
            notes: session.reviewerNotes ?? "",
          }));
        }
        if (session.reviewerEmail?.trim()) {
          const match = users.find(
            (user) =>
              user.email?.trim().toLowerCase() ===
              session.reviewerEmail?.trim().toLowerCase(),
          );
          if (match) {
            setFormValues((prev) => ({ ...prev, reviewerUserId: match.id }));
          }
        }
      } catch (err: unknown) {
        if (!cancelled) {
          setReviewersError(
            err instanceof ApiError
              ? err.message
              : err instanceof Error
                ? err.message
                : "Unable to load reviewers.",
          );
        }
      } finally {
        if (!cancelled) {
          setReviewersLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [step, sessionId, gameRuleName]);

  const kbInputSteps = useMemo(
    () => filterKbSectionInputSteps(inputSteps),
    [inputSteps],
  );

  useEffect(() => {
    if (activeInputSection >= kbInputSteps.length) {
      setActiveInputSection(Math.max(0, kbInputSteps.length - 1));
    }
  }, [activeInputSection, kbInputSteps.length]);

  const completedInputSections = useMemo(
    () => getCompletedSectionIndexes(kbInputSteps, inputFieldValues),
    [inputFieldValues, kbInputSteps],
  );

  const currentInputStep = kbInputSteps[activeInputSection];
  const isCurrentInputSectionValid = currentInputStep
    ? isInputSectionValid(currentInputStep, inputFieldValues)
    : false;
  const isLastInputSection =
    kbInputSteps.length === 0 || activeInputSection >= kbInputSteps.length - 1;

  const sectionClauseById = useMemo(() => {
    const map = new Map<
      string,
      {
        readonly sectionName: string;
        readonly tag: string | null;
        readonly description: string | null;
        readonly content: string;
      }
    >();
    for (const inputStep of inputSteps) {
      const content = inputStep.content?.trim();
      if (!content || inputStep.id === "programme") {
        continue;
      }
      map.set(inputStep.id, {
        sectionName: inputStep.name,
        tag: inputStep.tag,
        description: inputStep.description ?? null,
        content,
      });
    }
    return map;
  }, [inputSteps]);

  const openClausePreviewForInputStep = (inputStep: GenerateFlowInputStep) => {
    const preview = sectionClauseById.get(inputStep.id);
    if (!preview) {
      return;
    }
    setClausePreview(preview);
  };

  const currentAiSectionId = selectedSectionIds[activeAiSectionIndex] ?? null;
  const currentAiSectionClause = currentAiSectionId
    ? (sectionClauseById.get(currentAiSectionId) ?? null)
    : null;
  const currentAiResult = currentAiSectionId
    ? aiResults[currentAiSectionId]
    : undefined;
  const aiJobRunning = aiSessionStatus === "ai_running";
  const aiGeneratedCount = useMemo(() => {
    const fromProgress =
      aiProgress && aiProgress.total > 0 ? aiProgress.completed : null;
    if (fromProgress !== null && aiJobRunning) {
      return fromProgress;
    }
    return countAiGeneratedSections(selectedSectionIds, aiResults);
  }, [aiProgress, aiJobRunning, aiResults, selectedSectionIds]);
  const aiGenerationTotal = aiProgress?.total ?? selectedSectionIds.length;
  const aiGenerationPercent =
    aiGenerationTotal > 0
      ? Math.min(100, Math.round((aiGeneratedCount / aiGenerationTotal) * 100))
      : 0;
  const currentProcessingSectionId = aiProgress?.currentSectionId ?? null;
  const currentAiGenerationState = currentAiSectionId
    ? getSectionAiGenerationState(
        currentAiSectionId,
        aiResults,
        aiProgress,
        aiSessionStatus,
      )
    : "pending";
  const browsableAiSectionIndexes = useMemo(
    () =>
      selectedSectionIds
        .map((sectionId, index) => ({
          index,
          browsable: isSectionAiBrowsable(
            getSectionAiGenerationState(
              sectionId,
              aiResults,
              aiProgress,
              aiSessionStatus,
            ),
          ),
        }))
        .filter((row) => row.browsable)
        .map((row) => row.index),
    [aiProgress, aiResults, aiSessionStatus, selectedSectionIds],
  );
  const canGoAiPrev = browsableAiSectionIndexes.some(
    (index) => index < activeAiSectionIndex,
  );
  const canGoAiNext = browsableAiSectionIndexes.some(
    (index) => index > activeAiSectionIndex,
  );
  const aiSectionsCompleteCount = selectedSectionIds.filter((id) =>
    Boolean(aiSelectionsBySection[id]?.finalContent.trim()),
  ).length;
  const currentPlaceholderContent = currentAiSectionId
    ? (aiSelectionsBySection[currentAiSectionId]?.finalContent ?? "")
    : "";
  const hasCurrentPlaceholderContent = Boolean(
    currentPlaceholderContent.trim(),
  );
  const isCurrentPlaceholderSelected = currentAiSectionId
    ? aiSelectionsBySection[currentAiSectionId]?.selectedIndex === 0 &&
      Boolean(aiSelectionsBySection[currentAiSectionId]?.finalContent.trim())
    : false;

  const goAiPrevSection = useCallback(() => {
    const prevIndex = [...browsableAiSectionIndexes]
      .reverse()
      .find((index) => index < activeAiSectionIndex);
    if (prevIndex !== undefined) {
      setActiveAiSectionIndex(prevIndex);
      setEditingSectionId(null);
      setExpandedOptionKey(null);
    }
  }, [activeAiSectionIndex, browsableAiSectionIndexes]);

  const goAiNextSection = useCallback(() => {
    const nextIndex = browsableAiSectionIndexes.find(
      (index) => index > activeAiSectionIndex,
    );
    if (nextIndex !== undefined) {
      setActiveAiSectionIndex(nextIndex);
      setEditingSectionId(null);
      setExpandedOptionKey(null);
    }
  }, [activeAiSectionIndex, browsableAiSectionIndexes]);

  const retryAiGeneration = useCallback(() => {
    if (!sessionId) {
      return;
    }
    resetStepFourAiState();
    setAiInitialLoading(true);
    void (async () => {
      try {
        await startSessionAi(sessionId, { force: true });
        let status = await getSessionAiStatus(sessionId);
        setAiSessionStatus(status.status);
        setAiResults(status.aiResults);
        setAiProgress(status.progress);
        if (status.status === "ai_failed") {
          setAiError(status.error ?? "AI generation failed. Try again.");
        }
        setAiInitialLoading(false);
        while (status.status === "ai_running") {
          await sleep(2000);
          status = await getSessionAiStatus(sessionId);
          setAiSessionStatus(status.status);
          setAiResults(status.aiResults);
          setAiProgress(status.progress);
        }
        if (status.status === "ai_failed") {
          setAiError(status.error ?? "AI generation failed. Try again.");
        }
      } catch (err: unknown) {
        setAiError(
          err instanceof ApiError
            ? err.message
            : err instanceof Error
              ? err.message
              : "Unable to restart AI generation.",
        );
      } finally {
        setAiInitialLoading(false);
      }
    })();
  }, [resetStepFourAiState, sessionId]);

  useEffect(
    () => () => {
      previewDebounceRef.current.cancel();
    },
    [],
  );

  const queueDocumentPreview = useCallback(
    async (sectionOrder: readonly string[]) => {
      if (!sessionId || sectionOrder.length === 0) {
        return;
      }
      const requestId = previewRequestIdRef.current + 1;
      previewRequestIdRef.current = requestId;
      setDocumentPreviewLoading(true);
      setDocumentPreviewError(null);
      try {
        const { docxBase64 } = await previewGenerationSession(
          sessionId,
          sectionOrder,
        );
        if (requestId !== previewRequestIdRef.current) {
          return;
        }
        await openDraftInHost(docxBase64);
      } catch (err: unknown) {
        if (requestId !== previewRequestIdRef.current) {
          return;
        }
        setDocumentPreviewError(
          err instanceof ApiError
            ? err.message
            : err instanceof Error
              ? err.message
              : "Unable to update document preview in Word.",
        );
      } finally {
        if (requestId === previewRequestIdRef.current) {
          setDocumentPreviewLoading(false);
        }
      }
    },
    [sessionId],
  );

  const scheduleStepFourPreviewAndPersist = useCallback(
    (
      sectionOrder: readonly string[],
      nextSelections: Record<string, SectionAiSelection>,
    ) => {
      if (sectionOrder.length === 0) {
        return;
      }
      previewDebounceRef.current.schedule(() => {
        const seq = ++persistPreviewSeqRef.current;
        void (async () => {
          if (sessionId) {
            try {
              await patchGenerationSession(sessionId, {
                sectionOrder,
                aiSelections: nextSelections,
              });
            } catch {
              if (seq === persistPreviewSeqRef.current) {
                setDocumentPreviewError(
                  "Unable to save AI selection. Preview was not updated.",
                );
              }
              return;
            }
          }
          if (seq !== persistPreviewSeqRef.current) {
            return;
          }
          await queueDocumentPreview(sectionOrder);
        })();
      });
    },
    [queueDocumentPreview, sessionId],
  );

  const selectAiOption = useCallback(
    (
      sectionId: string,
      optionIndex: number,
      content: string,
      blocks?: readonly Record<string, unknown>[] | null,
    ) => {
      const nextSelection: SectionAiSelection = {
        selectedIndex: optionIndex,
        finalContent: content,
        wasEdited: false,
        finalStructure:
          blocks && blocks.length > 0
            ? { schema_version: 1, blocks: [...blocks] }
            : (aiSelectionsBySection[sectionId]?.finalStructure ?? null),
      };
      const nextSelections = {
        ...aiSelectionsBySection,
        [sectionId]: nextSelection,
      };
      const nextOrder = appendAiSelectionOrder(aiSelectionOrder, sectionId);
      setAiSelectionsBySection(nextSelections);
      setAiSelectionOrder(nextOrder);
      if (step === 4) {
        scheduleStepFourPreviewAndPersist(nextOrder, nextSelections);
      }
      setEditingSectionId(null);
      setExpandedOptionKey(null);
    },
    [
      aiSelectionOrder,
      aiSelectionsBySection,
      scheduleStepFourPreviewAndPersist,
      step,
    ],
  );

  const startEditingAiOption = useCallback(
    (sectionId: string, optionIndex: number, content: string) => {
      setEditingSectionId(sectionId);
      setEditDraft(content);
      setAiSelectionsBySection((prev) => ({
        ...prev,
        [sectionId]: {
          selectedIndex: optionIndex,
          finalContent: content,
          wasEdited: prev[sectionId]?.wasEdited ?? false,
        },
      }));
    },
    [],
  );

  const saveAiEdit = useCallback(
    (sectionId: string, optionIndex: number) => {
      const trimmed = editDraft.trim();
      if (!trimmed) {
        return;
      }
      const nextSelection: SectionAiSelection = {
        selectedIndex: optionIndex,
        finalContent: trimmed,
        wasEdited: true,
        finalStructure: null,
      };
      const nextSelections = {
        ...aiSelectionsBySection,
        [sectionId]: nextSelection,
      };
      const nextOrder = appendAiSelectionOrder(aiSelectionOrder, sectionId);
      setAiSelectionsBySection(nextSelections);
      setAiSelectionOrder(nextOrder);
      if (step === 4) {
        scheduleStepFourPreviewAndPersist(nextOrder, nextSelections);
      }
      setEditingSectionId(null);
    },
    [
      aiSelectionOrder,
      aiSelectionsBySection,
      editDraft,
      scheduleStepFourPreviewAndPersist,
      step,
    ],
  );

  const handleStepFivePreview = useCallback(() => {
    if (!sessionId || orderedSections.length === 0) {
      setDocumentPreviewError(
        "Select and order at least one section to preview.",
      );
      return;
    }
    previewDebounceRef.current.cancel();
    void queueDocumentPreview(orderedSections);
  }, [orderedSections, queueDocumentPreview, sessionId]);

  const selectedReviewer = useMemo(
    () =>
      reviewers.find((user) => user.id === formValues.reviewerUserId) ?? null,
    [formValues.reviewerUserId, reviewers],
  );
  const selectedReviewerEmail = selectedReviewer?.email?.trim() ?? "";
  const missingStepFiveMetadataIds = useMemo(
    () => orderedSections.filter((id) => !sectionRowById[id]),
    [orderedSections, sectionRowById],
  );
  const isStepFiveMetadataPending =
    orderedSections.length > 0 &&
    missingStepFiveMetadataIds.length > 0 &&
    fieldStepsLoading &&
    !fieldStepsError;

  const canProceedToNextStep = useMemo(() => {
    if (isSaving) {
      return false;
    }
    if (step === 1) {
      return Boolean(gameRuleTitle.trim() && gameRuleName.trim());
    }
    if (step === 2) {
      return selectedSectionIds.length > 0 && !sectionsLoading;
    }
    if (step === 3) {
      return (
        !fieldStepsLoading &&
        kbInputSteps.length > 0 &&
        areAllInputSectionsValid(kbInputSteps, inputFieldValues)
      );
    }
    if (step === 4) {
      return (
        aiSessionStatus === "ai_ready" &&
        allSectionsHaveAiSelection(selectedSectionIds, aiSelectionsBySection)
      );
    }
    if (step === 5) {
      return (
        Boolean(fileName.trim() && selectedReviewerEmail) &&
        orderedSections.length > 0 &&
        !reviewersLoading &&
        (!saveAsTemplate || Boolean(saveTemplateName.trim()))
      );
    }
    return false;
  }, [
    aiSelectionsBySection,
    aiSessionStatus,
    fileName,
    formValues.reviewerUserId,
    orderedSections.length,
    reviewersLoading,
    selectedReviewerEmail,
    gameRuleName,
    gameRuleTitle,
    fieldStepsLoading,
    inputFieldValues,
    kbInputSteps,
    isSaving,
    selectedSectionIds,
    sectionsLoading,
    saveAsTemplate,
    saveTemplateName,
    step,
  ]);

  const setInputFieldValue = useCallback(
    (
      inputStep: GenerateFlowInputStep,
      field: GenerateFlowField,
      value: string,
    ) => {
      const key = fieldValueKey(inputStep.id, field.key);
      setInputFieldValues((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  const currentCopy = getStepCopy(step, {
    fromTemplate: flowFromTemplate,
    placeholderOnly: isPlaceholderMode,
  });

  const goNext = () => {
    setError(null);
    setSuccess(null);
    if (step === 1) {
      if (!gameRuleTitle.trim()) {
        setError("Game Rule Title is required.");
        return;
      }
      if (!gameRuleName.trim()) {
        setError("Save as Game Rule is required.");
        return;
      }
      const nextContentMode = placeholderOnlyChecked
        ? CONTENT_MODE_PLACEHOLDER_ONLY
        : CONTENT_MODE_AI_SUGGESTIONS;
      setIsSaving(true);
      void (async () => {
        try {
          let activeSessionId = sessionId;
          if (!activeSessionId) {
            const createOptions =
              flowFromTemplate && fromTemplateId
                ? {
                    templateId: fromTemplateId,
                    flowType: FLOW_TYPE_FROM_TEMPLATE,
                    contentMode: nextContentMode,
                  }
                : { contentMode: nextContentMode };
            const created = await createGenerationSession(
              gameRuleName.trim(),
              gameRuleTitle.trim(),
              createOptions,
            );
            activeSessionId = created.id;
            setSessionId(activeSessionId);
            persistGenerationSessionId(activeSessionId);
            if (created.selectedSectionIds.length > 0) {
              setSelectedSectionIds([...created.selectedSectionIds]);
              setOrderedSections(
                created.sectionOrder.length > 0
                  ? [...created.sectionOrder]
                  : [...created.selectedSectionIds],
              );
            }
          }
          const nextStep = flowFromTemplate ? 3 : 2;
          await patchGenerationSession(activeSessionId, {
            currentStep: nextStep,
            ruleName: gameRuleName.trim(),
            intentText: gameRuleTitle.trim(),
            contentMode: nextContentMode,
            ...(flowFromTemplate && fromTemplateId
              ? {
                  flowType: FLOW_TYPE_FROM_TEMPLATE,
                  templateId: fromTemplateId,
                }
              : {}),
          });
          setContentMode(nextContentMode);
          if (flowFromTemplate) {
            setActiveInputSection(0);
          }
          setStep(nextStep);
        } catch (err: unknown) {
          setError(
            err instanceof ApiError
              ? err.message
              : err instanceof Error
                ? err.message
                : "Unable to save your game rule. Try again.",
          );
        } finally {
          setIsSaving(false);
        }
      })();
      return;
    }
    if (step === 2 && selectedSectionIds.length === 0) {
      setError("Select at least one Knowledgebase section.");
      return;
    }
    if (step === 4) {
      if (!sessionId) {
        setError(
          "Generation session expired. Go back to Step 1 and try again.",
        );
        return;
      }
      if (
        !allSectionsHaveAiSelection(selectedSectionIds, aiSelectionsBySection)
      ) {
        setError("Select AI content for every section before continuing.");
        return;
      }
      if (aiSessionStatus !== "ai_ready") {
        setError("Wait for AI suggestions to finish before continuing.");
        return;
      }
      setIsSaving(true);
      void (async () => {
        try {
          const orderForStepFive =
            aiSelectionOrder.length > 0
              ? aiSelectionOrder
              : deriveAiSelectionOrder(
                  selectedSectionIds,
                  aiSelectionsBySection,
                  selectedSectionIds,
                );
          await patchGenerationSession(sessionId, {
            currentStep: 5,
            aiSelections: aiSelectionsBySection,
            sectionOrder: orderForStepFive,
          });
          setOrderedSections([...orderForStepFive]);
          setStep(5);
        } catch (err: unknown) {
          setError(
            err instanceof ApiError
              ? err.message
              : err instanceof Error
                ? err.message
                : "Unable to save AI selections. Try again.",
          );
        } finally {
          setIsSaving(false);
        }
      })();
      return;
    }
    if (step === 2) {
      if (!sessionId) {
        setError(
          "Generation session expired. Go back to Step 1 and try again.",
        );
        return;
      }
      setIsSaving(true);
      void (async () => {
        try {
          await patchGenerationSession(sessionId, {
            currentStep: 3,
            selectedSectionIds,
            sectionOrder: selectedSectionIds,
          });
          resetStep3FieldsOnLoadRef.current = true;
          setInputFieldValues({});
          setActiveInputSection(0);
          setOrderedSections(selectedSectionIds);
          setStep(3);
        } catch (err: unknown) {
          setError(
            err instanceof ApiError
              ? err.message
              : err instanceof Error
                ? err.message
                : "Unable to save section selection. Try again.",
          );
        } finally {
          setIsSaving(false);
        }
      })();
      return;
    }
    if (step === 3) {
      if (!sessionId) {
        setError(
          "Generation session expired. Go back to Step 1 and try again.",
        );
        return;
      }
      if (!areAllInputSectionsValid(kbInputSteps, inputFieldValues)) {
        setError("Complete all required input fields before continuing.");
        return;
      }
      setIsSaving(true);
      void (async () => {
        try {
          await patchGenerationSession(sessionId, {
            currentStep: 4,
            programme: buildProgrammeFromStep1({
              gameRuleName,
              sponsorName: formValues.sponsorName,
              administratorName: formValues.administratorName,
              savedProgramme: savedSessionProgramme,
            }),
            fieldValues: buildFieldValuesPayload(
              kbInputSteps,
              inputFieldValues,
            ),
            contentMode,
          });
          if (isPlaceholderMode) {
            await resolveSessionPlaceholders(sessionId);
          }
          resetStepFourAiState();
          setAiInitialLoading(true);
          setActiveAiSectionIndex(0);
          setStep(4);
        } catch (err: unknown) {
          setError(
            err instanceof ApiError
              ? err.message
              : err instanceof Error
                ? err.message
                : "Unable to save input fields. Try again.",
          );
        } finally {
          setIsSaving(false);
        }
      })();
      return;
    }
    if (step === 5) {
      if (!sessionId) {
        setError(
          "Generation session expired. Go back to Step 1 and try again.",
        );
        return;
      }
      if (!fileName.trim()) {
        setError("Enter a document file name before generating.");
        return;
      }
      if (!selectedReviewerEmail) {
        setError("Select a reviewer with a valid email address.");
        return;
      }
      if (orderedSections.length === 0) {
        setError("At least one section is required to generate the document.");
        return;
      }
      if (saveAsTemplate && !saveTemplateName.trim()) {
        setError("Enter a template name or uncheck Save as template.");
        return;
      }
      setIsSaving(true);
      void (async () => {
        try {
          await patchGenerationSession(sessionId, {
            currentStep: 5,
            fileName: fileName.trim(),
            reviewerEmail: selectedReviewerEmail,
            reviewerNotes: formValues.notes.trim() || undefined,
            sectionOrder: orderedSections,
          });
          const result = await commitGenerationSession(sessionId);
          if (saveAsTemplate && saveTemplateName.trim()) {
            try {
              const saved = await saveKbGenerationTemplate(
                saveTemplateName.trim(),
                orderedSections,
                {
                  sourceSessionId: sessionId,
                  sourceDocumentId: result.documentId,
                },
              );
              clearGenerationSessionId();
              navigate("/prizelogic/listing", {
                state: {
                  generateSuccessMessage: `Document "${result.fileName}" was generated and sent for review. Template "${saved.name}" saved (v${saved.version}).`,
                },
              });
              return;
            } catch {
              clearGenerationSessionId();
              navigate("/prizelogic/listing", {
                state: {
                  generateSuccessMessage: `Document "${result.fileName}" was generated, but the template could not be saved.`,
                },
              });
              return;
            }
          }
          clearGenerationSessionId();
          navigate("/prizelogic/listing", {
            state: {
              generateSuccessMessage: `Document "${result.fileName}" was generated and sent for review.`,
            },
          });
        } catch (err: unknown) {
          setError(
            err instanceof ApiError
              ? err.message
              : err instanceof Error
                ? err.message
                : "Unable to generate the document. Try again.",
          );
        } finally {
          setIsSaving(false);
        }
      })();
      return;
    }
    setStep((prev) => (prev + 1) as GenerateStep);
  };

  const goBack = () => {
    setError(null);
    setSuccess(null);
    if (step === 1) {
      navigate("/prizelogic/generate-document");
      return;
    }
    if (step === 3 && flowFromTemplate) {
      setStep(1);
      return;
    }
    setStep((prev) => (prev - 1) as GenerateStep);
  };

  const toggleSection = (id: string) => {
    setSelectedSectionIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    );
  };

  const moveSection = (index: number, direction: -1 | 1) => {
    setOrderedSections((prev) => {
      const target = index + direction;
      if (target < 0 || target >= prev.length) {
        return prev;
      }
      const next = [...prev];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const sectionById = (id: string): SectionRow => {
    const known = sectionRowById[id];
    if (known) return known;
    return {
      id,
      name: id,
      description: "",
      fields: 0,
      tag: "General",
    };
  };

  const progressHeader = (
    <div className="-mx-4 -mt-4 border-b border-[var(--border)] bg-background px-3 pt-2">
      <div className="grid grid-cols-5 gap-1">
        {[1, 2, 3, 4, 5].map((item) => (
          <div
            key={item}
            className={`h-1 rounded-full ${
              item < step
                ? "bg-emerald-500"
                : item === step
                  ? "bg-violet-700"
                  : "bg-muted"
            }`}
          />
        ))}
      </div>
      <div className="py-4">
        <h2 className="text-sm font-bold leading-tight text-primary">
          {currentCopy.title}
        </h2>
        <p className="mt-0.5 text-xs text-muted-foreground">
          {currentCopy.subtitle}
        </p>
      </div>
    </div>
  );

  const stepOne = (
    <section className="space-y-5 pt-4">
      <div className="rounded border border-[var(--border)] bg-card px-4 py-3">
        <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground">
          What are you preparing?
        </p>
        <p className="mt-2 text-xs leading-5 text-muted-foreground">
          Enter a title and internal name for this game rule, then choose which
          Knowledgebase sections to include in the generated document.
        </p>
      </div>

      <label
        className="block text-xs font-bold text-foreground"
        htmlFor="generate-game-rule-title"
      >
        Game Rule Title <span className="text-destructive">*</span>
        <input
          id="generate-game-rule-title"
          className="mt-1 w-full rounded border border-input bg-background px-3 py-2 text-xs font-normal"
          value={gameRuleTitle}
          onChange={(event) => setGameRuleTitle(event.target.value)}
          placeholder="e.g. Summer Sip Sweepstakes Official Rules"
        />
      </label>

      <label
        className="block text-xs font-bold text-foreground"
        htmlFor="generate-game-rule-name"
      >
        Save as Game Rule <span className="text-destructive">*</span>
        <input
          id="generate-game-rule-name"
          className="mt-1 w-full rounded border border-input bg-background px-3 py-2 text-xs font-normal"
          value={gameRuleName}
          onChange={(event) => setGameRuleName(event.target.value)}
        />
      </label>

      {sourceTemplateName ? (
        <div className="rounded border border-violet-200 bg-violet-50 px-3 py-2 text-[11px] text-violet-900">
          Using template:{" "}
          <span className="font-semibold">{sourceTemplateName}</span>
          {" · "}
          {selectedSectionIds.length} section
          {selectedSectionIds.length === 1 ? "" : "s"} pre-selected
        </div>
      ) : null}

      <label className="flex items-start gap-2 rounded border border-[var(--border)] bg-card px-3 py-2">
        <input
          type="checkbox"
          className="mt-0.5 accent-violet-700"
          checked={placeholderOnlyChecked}
          onChange={(event) => setPlaceholderOnlyChecked(event.target.checked)}
        />
        <span className="text-xs">
          <span className="block font-bold text-foreground">
            Placeholder only
          </span>
          <span className="mt-0.5 block text-[11px] font-normal leading-4 text-muted-foreground">
            Skip AI suggestions and fill sections from your Step 3 input field
            values only.
          </span>
        </span>
      </label>
    </section>
  );

  const stepTwo = (
    <section className="space-y-4 pt-3">
      <div className="space-y-1">
        <p className="text-xs font-bold text-foreground">Search Sections</p>
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-sky-600" />
          <input
            className="w-full rounded border border-input bg-background py-2 pl-9 pr-3 text-xs"
            placeholder="Search by section name, tag, or description..."
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>
        <p className="text-[11px] text-muted-foreground">
          {sectionsLoading
            ? "Loading Knowledgebase sections…"
            : searchQuery
              ? `${sectionsTotal} section${sectionsTotal === 1 ? "" : "s"} match "${searchQuery}"`
              : `${sectionsTotal} Knowledgebase section${sectionsTotal === 1 ? "" : "s"} available`}
        </p>
      </div>

      {sectionsError ? (
        <p className="rounded border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
          {sectionsError}
        </p>
      ) : null}

      {/* <div className="rounded border border-[var(--border)] bg-card px-4 py-3">
        <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground">
          Knowledgebase
        </p>
        <p className="mt-1 text-[11px] leading-4 text-muted-foreground">
          Select sections to include in your document. Use the search bar above
          to filter the Knowledgebase.
        </p>
      </div> */}

      {sectionsLoading ? (
        <p className="rounded border border-[var(--border)] bg-card px-3 py-4 text-center text-xs text-muted-foreground">
          Loading sections…
        </p>
      ) : kbSections.length === 0 ? (
        <p className="rounded border border-[var(--border)] bg-card px-3 py-4 text-center text-xs text-muted-foreground">
          No sections match your search.
        </p>
      ) : (
        <div className="space-y-2">
          {kbSections.map((section) => {
            const checked = selectedSectionIds.includes(section.id);
            return (
              <label
                key={section.id}
                className="flex items-center gap-2 rounded border border-[var(--border)] bg-card px-3 py-2"
              >
                <GripVertical className="h-4 w-4 shrink-0 text-muted-foreground/50" />
                <input
                  type="checkbox"
                  className="accent-violet-700"
                  checked={checked}
                  onChange={() => toggleSection(section.id)}
                />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-xs font-bold text-primary">
                    {section.name}
                  </span>
                  <span className="block truncate text-[11px] text-muted-foreground">
                    {section.description} {section.fields} input fields
                  </span>
                </span>
                <span
                  className={`rounded-full px-2 py-1 text-[10px] font-bold text-white ${tagColor(section.tag)}`}
                >
                  {section.tag}
                </span>
              </label>
            );
          })}
        </div>
      )}

      {!sectionsLoading && sectionsTotal > 0 ? (
        <div className="rounded border border-[var(--border)] bg-card px-3 py-2 text-center text-[11px] text-muted-foreground">
          <div className="flex items-center justify-between">
            <span>
              Showing {rangeStart}–{rangeEnd} of {sectionsTotal}
              {searchQuery ? " matching" : ""}
            </span>
            <span className="space-x-1">
              <button
                className="rounded border px-2 py-1 text-muted-foreground disabled:cursor-default disabled:opacity-40"
                type="button"
                disabled={clampedKbPage <= 1}
                onClick={() => setKbPage((page) => Math.max(1, page - 1))}
              >
                ‹
              </button>
              {kbPageNumbers.map((pageNumber, index) =>
                pageNumber === "…" ? (
                  <button
                    key={`ellipsis-${index}`}
                    className="rounded border px-2 py-1"
                    type="button"
                    disabled
                  >
                    …
                  </button>
                ) : (
                  <button
                    key={pageNumber}
                    className={`rounded px-2 py-1 ${
                      pageNumber === clampedKbPage
                        ? "bg-violet-700 font-bold text-white"
                        : "border"
                    }`}
                    type="button"
                    onClick={() => setKbPage(pageNumber)}
                  >
                    {pageNumber}
                  </button>
                ),
              )}
              <button
                className="rounded border px-2 py-1 disabled:cursor-default disabled:opacity-40"
                type="button"
                disabled={clampedKbPage >= totalKbPages}
                onClick={() =>
                  setKbPage((page) => Math.min(totalKbPages, page + 1))
                }
              >
                ›
              </button>
            </span>
          </div>
          <p className="mt-3">
            {selectedSectionIds.length} of {sectionsTotal} Knowledgebase
            sections selected
          </p>
        </div>
      ) : null}
    </section>
  );

  const renderInputField = (
    inputStep: GenerateFlowInputStep,
    field: GenerateFlowField,
  ) => {
    const value = getFieldValue(inputStep.id, field, inputFieldValues);
    const fieldId = `field-${inputStep.id}-${field.key}`;

    if (field.type === "select") {
      return (
        <label
          key={field.key}
          className="block text-xs font-bold text-foreground"
          htmlFor={fieldId}
        >
          {field.label}
          {field.docPlaceholder?.trim() ? (
            <span className="ml-1 font-normal text-muted-foreground">
              ({field.docPlaceholder})
            </span>
          ) : null}
          {field.required ? <span className="text-destructive"> *</span> : null}
          <select
            id={fieldId}
            className="mt-1 w-full rounded border border-input bg-background px-3 py-2 text-xs font-normal"
            value={value}
            onChange={(event) =>
              setInputFieldValue(inputStep, field, event.target.value)
            }
          >
            <option value="">Select an option</option>
            {(field.options ?? []).map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
          {field.hint?.trim() ? (
            <p className="mt-0.5 text-[10px] font-normal text-muted-foreground">
              {field.hint}
            </p>
          ) : null}
        </label>
      );
    }

    const inputType =
      field.type === "number"
        ? "number"
        : field.type === "date"
          ? "date"
          : "text";

    return (
      <label
        key={field.key}
        className="block text-xs font-bold text-foreground"
        htmlFor={fieldId}
      >
        {field.label}
        {field.docPlaceholder?.trim() ? (
          <span className="ml-1 font-normal text-muted-foreground">
            ({field.docPlaceholder})
          </span>
        ) : null}
        {field.required ? <span className="text-destructive"> *</span> : null}
        <input
          id={fieldId}
          type={inputType}
          className="mt-1 w-full rounded border border-input bg-background px-3 py-2 text-xs font-normal"
          value={value}
          onChange={(event) =>
            setInputFieldValue(inputStep, field, event.target.value)
          }
        />
        {field.hint?.trim() ? (
          <p className="mt-0.5 text-[10px] font-normal text-muted-foreground">
            {field.hint}
          </p>
        ) : null}
      </label>
    );
  };

  const stepThree = (
    <section className="space-y-4 pt-3">
      {fieldStepsError ? (
        <p className="rounded border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
          {fieldStepsError}
        </p>
      ) : null}
      {fieldStepsLoading ? (
        <p className="rounded border border-[var(--border)] bg-card px-3 py-4 text-center text-xs text-muted-foreground">
          Loading input fields…
        </p>
      ) : kbInputSteps.length === 0 ? (
        <p className="rounded border border-[var(--border)] bg-card px-3 py-4 text-center text-xs text-muted-foreground">
          No sections selected. Go back to Step 2.
        </p>
      ) : (
        <>
          <div className="rounded bg-muted/40 px-3 py-2 text-center text-xs text-muted-foreground">
            Section {activeInputSection + 1} of {kbInputSteps.length}
          </div>
          <div className="flex flex-wrap justify-center gap-2">
            {kbInputSteps.map((inputStep, index) => {
              const complete = completedInputSections.has(index);
              const isCurrent = index === activeInputSection;
              return (
                <button
                  key={inputStep.id}
                  type="button"
                  title={inputStep.name}
                  className={`flex h-7 w-7 items-center justify-center rounded-full border text-[11px] font-semibold ${
                    complete && !isCurrent
                      ? "border-emerald-600 bg-emerald-600 text-white"
                      : isCurrent
                        ? "border-violet-700 bg-violet-700 text-white"
                        : "border-[var(--border)] bg-background text-muted-foreground"
                  }`}
                  onClick={() => setActiveInputSection(index)}
                >
                  {complete && !isCurrent ? (
                    <Check className="h-3.5 w-3.5" />
                  ) : (
                    index + 1
                  )}
                </button>
              );
            })}
          </div>
          {currentInputStep ? (
            <div className="text-center">
              <p className="text-xs font-bold text-primary">
                {currentInputStep.name}
              </p>
              <p className="text-[11px] text-muted-foreground">
                {currentInputStep.tag
                  ? `Tag: ${currentInputStep.tag} · ${currentInputStep.fields.length} input field${currentInputStep.fields.length === 1 ? "" : "s"}`
                  : `${currentInputStep.fields.length} input field${currentInputStep.fields.length === 1 ? "" : "s"}`}
              </p>
              {sectionClauseById.has(currentInputStep.id) ? (
                <button
                  type="button"
                  className="mt-2 rounded border border-primary px-2 py-1 text-[11px] font-bold text-primary"
                  onClick={() =>
                    openClausePreviewForInputStep(currentInputStep)
                  }
                >
                  View section clause
                </button>
              ) : null}
            </div>
          ) : null}
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              disabled={activeInputSection === 0}
              className="rounded border border-input bg-background px-3 py-2 text-xs font-semibold text-muted-foreground disabled:opacity-50"
              onClick={() =>
                setActiveInputSection((value) => Math.max(0, value - 1))
              }
            >
              ← Previous
            </button>
            <button
              type="button"
              disabled={!isCurrentInputSectionValid || isLastInputSection}
              className="rounded bg-violet-700 px-3 py-2 text-xs font-bold text-white disabled:opacity-50"
              onClick={() =>
                setActiveInputSection((value) =>
                  Math.min(kbInputSteps.length - 1, value + 1),
                )
              }
            >
              {isLastInputSection ? "Last Section" : "Next →"}
            </button>
          </div>
          <div className="space-y-2">
            {currentInputStep?.fields.map((field) =>
              renderInputField(currentInputStep, field),
            )}
          </div>
          {currentInputStep ? (
            <p className="text-[9.5px] text-muted-foreground/80">
              From section: {currentInputStep.name}
            </p>
          ) : null}
        </>
      )}
    </section>
  );

  const stepFour = (
    <section className="space-y-4 pt-3">
      {selectedSectionIds.length === 0 ? (
        <div className="rounded border border-amber-500 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          No sections selected. Go back to Step 2 and choose sections.
        </div>
      ) : aiInitialLoading ? (
        <div className="flex flex-col items-center gap-2 rounded border border-[var(--border)] bg-card px-3 py-8 text-center">
          <Loader2
            className="h-5 w-5 animate-spin text-muted-foreground"
            aria-hidden
          />
          <p className="text-xs text-muted-foreground">
            Loading AI suggestions…
          </p>
        </div>
      ) : (
        <>
          {(aiJobRunning || aiGeneratedCount > 0) &&
          !isPlaceholderMode &&
          aiGeneratedCount !== aiGenerationTotal ? (
            <div className="space-y-2 rounded border border-violet-200 bg-violet-50/60 px-3 py-3">
              <div className="flex flex-wrap items-center justify-between gap-2 text-[11px] text-violet-900">
                <span className="font-semibold">
                  {aiGeneratedCount} of {aiGenerationTotal} sections generated
                </span>
                {aiJobRunning && currentProcessingSectionId ? (
                  <span className="text-violet-700">
                    Processing: {sectionById(currentProcessingSectionId).name}
                  </span>
                ) : aiJobRunning ? (
                  <span className="text-violet-700">
                    AI generation in progress…
                  </span>
                ) : null}
              </div>
              <div
                className="h-2 w-full overflow-hidden rounded-full bg-violet-100"
                role="progressbar"
                aria-valuenow={aiGeneratedCount}
                aria-valuemin={0}
                aria-valuemax={aiGenerationTotal}
                aria-label="AI generation progress"
              >
                <div
                  className="h-full rounded-full bg-violet-700 transition-all duration-500 ease-out"
                  style={{ width: `${aiGenerationPercent}%` }}
                />
              </div>
              {aiJobRunning ? (
                <p className="text-[10px] leading-snug text-violet-800">
                  Completed sections are available below. Select AI content
                  while the rest generate.
                </p>
              ) : null}
            </div>
          ) : null}
          {aiError ? (
            <div className="space-y-2 rounded border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
              <p>{aiError}</p>
              <button
                type="button"
                className="rounded border border-destructive px-2 py-1 text-[11px] font-bold"
                onClick={retryAiGeneration}
                disabled={aiJobRunning || aiInitialLoading}
              >
                Retry AI generation
              </button>
            </div>
          ) : null}
          <div className="rounded border border-sky-200 bg-sky-50 px-3 py-2 text-[11px] leading-4 text-sky-900">
            Preview only — selected sections open in Word in the order you
            select them. Nothing is saved until final generate.
          </div>
          {documentPreviewError ? (
            <div className="rounded border border-destructive/40 bg-destructive/10 px-3 py-2 text-[11px] text-destructive">
              {documentPreviewError}
            </div>
          ) : null}
          <div className="rounded bg-muted/40 px-3 py-2 text-center text-xs text-muted-foreground">
            {aiSectionsCompleteCount} of {selectedSectionIds.length} sections —
            {isPlaceholderMode
              ? " placeholder content ready"
              : " AI option selected"}
          </div>
          <div className="flex max-h-24 flex-wrap justify-center gap-1.5 overflow-y-auto px-1">
            {selectedSectionIds.map((sectionId, index) => {
              const userSelected = Boolean(
                aiSelectionsBySection[sectionId]?.finalContent.trim(),
              );
              const generationState = getSectionAiGenerationState(
                sectionId,
                aiResults,
                aiProgress,
                aiSessionStatus,
              );
              const browsable = isSectionAiBrowsable(generationState);
              const isCurrent = index === activeAiSectionIndex;
              let pillClass =
                "flex h-7 w-7 items-center justify-center rounded-full border text-[10px] font-semibold transition-colors ";
              if (userSelected && !isCurrent) {
                pillClass += "border-emerald-600 bg-emerald-600 text-white";
              } else if (isCurrent) {
                pillClass +=
                  "border-violet-700 bg-violet-700 text-white shadow-sm";
              } else if (generationState === "ready") {
                pillClass +=
                  "border-emerald-600 bg-background text-emerald-700 hover:bg-emerald-50";
              } else if (generationState === "generating") {
                pillClass +=
                  "border-violet-500 bg-violet-100 text-violet-800 animate-pulse";
              } else if (generationState === "failed") {
                pillClass +=
                  "border-destructive bg-destructive/10 text-destructive";
              } else {
                pillClass +=
                  "cursor-not-allowed border-[var(--border)] bg-muted/50 text-muted-foreground opacity-50";
              }
              const titleParts = [sectionRowById[sectionId]?.name ?? sectionId];
              if (generationState === "pending") {
                titleParts.push("(AI not ready yet)");
              } else if (generationState === "generating") {
                titleParts.push("(generating…)");
              }
              const previewOrderIndex = aiSelectionOrder.indexOf(sectionId);
              return (
                <button
                  key={sectionId}
                  type="button"
                  title={titleParts.join(" ")}
                  disabled={!browsable}
                  className={pillClass}
                  onClick={() => {
                    if (!browsable) {
                      return;
                    }
                    setActiveAiSectionIndex(index);
                    setEditingSectionId(null);
                    setExpandedOptionKey(null);
                  }}
                >
                  {generationState === "generating" && isCurrent ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                  ) : userSelected && previewOrderIndex >= 0 ? (
                    previewOrderIndex + 1
                  ) : (
                    index + 1
                  )}
                </button>
              );
            })}
          </div>
          <div className="text-center">
            <p className="text-xs font-bold text-primary">
              Section {activeAiSectionIndex + 1} of {selectedSectionIds.length}
            </p>
            <p className="text-[11px] text-muted-foreground">
              {currentAiSectionId
                ? sectionById(currentAiSectionId).name
                : "Section"}
            </p>
            <button
              type="button"
              disabled={!currentAiSectionClause}
              className="mt-2 rounded border border-primary px-2 py-1 text-[11px] font-bold text-primary disabled:opacity-50"
              onClick={() => {
                if (currentAiSectionClause) {
                  setClausePreview(currentAiSectionClause);
                }
              }}
            >
              View section clause
            </button>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              disabled={!canGoAiPrev}
              className="rounded border border-input bg-background px-3 py-2 text-xs font-semibold disabled:opacity-50"
              onClick={goAiPrevSection}
            >
              ← Previous
            </button>
            <button
              type="button"
              disabled={!canGoAiNext}
              className="rounded bg-violet-700 px-3 py-2 text-xs font-bold text-white disabled:opacity-50"
              onClick={goAiNextSection}
            >
              Next →
            </button>
          </div>
          <div>
            <p className="mb-2 text-xs font-bold text-primary">
              {isPlaceholderMode ? "Section Draft Content" : "AI Suggestions"}
            </p>
            {isPlaceholderMode ? (
              <div className="rounded border border-[var(--border)] bg-card px-3 py-3">
                <p className="whitespace-pre-wrap text-[11px] leading-4 text-foreground">
                  {currentAiSectionId
                    ? currentPlaceholderContent ||
                      "No Section Draft Content for this section."
                    : "Select a section above."}
                </p>
                <div className="mt-3 flex items-center gap-2">
                  <button
                    type="button"
                    disabled={
                      !currentAiSectionId || !hasCurrentPlaceholderContent
                    }
                    className={`ml-auto rounded border px-2 py-1 text-[11px] font-bold ${
                      isCurrentPlaceholderSelected
                        ? "border-emerald-600 bg-emerald-50 text-emerald-700"
                        : "border-violet-700 text-violet-700"
                    } disabled:cursor-not-allowed disabled:opacity-50`}
                    onClick={() =>
                      currentAiSectionId &&
                      hasCurrentPlaceholderContent &&
                      selectAiOption(
                        currentAiSectionId,
                        0,
                        currentPlaceholderContent,
                      )
                    }
                  >
                    {/* {isCurrentPlaceholderSelected ? "Selected" : "Select"} */}
                    Apply Section Draft Content
                  </button>
                </div>
              </div>
            ) : currentAiGenerationState === "pending" ? (
              <div className="rounded border border-[var(--border)] bg-card px-3 py-3 text-[11px] text-muted-foreground">
                {aiJobRunning ? (
                  <>
                    Waiting for AI to generate this section. You can review
                    completed sections using the numbered pills above (
                    {aiGeneratedCount} of {aiGenerationTotal} ready).
                  </>
                ) : (
                  "No AI options available for this section yet."
                )}
              </div>
            ) : currentAiGenerationState === "generating" ? (
              <div className="flex items-center gap-2 rounded border border-violet-200 bg-violet-50 px-3 py-3 text-[11px] text-violet-800">
                <Loader2
                  className="h-4 w-4 animate-spin shrink-0"
                  aria-hidden
                />
                Generating options for this section…
              </div>
            ) : !currentAiResult || currentAiResult.options.length === 0 ? (
              <div className="rounded border border-[var(--border)] bg-card px-3 py-3 text-[11px] text-muted-foreground">
                {currentAiResult?.error
                  ? currentAiResult.error
                  : "No AI options available for this section."}
              </div>
            ) : (
              <div className="space-y-2">
                {currentAiResult.options.map((option, optionIndex) => {
                  const optionKey = `${currentAiSectionId}::${optionIndex}`;
                  const isExpanded = expandedOptionKey === optionKey;
                  const isSelected =
                    aiSelectionsBySection[currentAiSectionId ?? ""]
                      ?.selectedIndex === optionIndex &&
                    Boolean(
                      aiSelectionsBySection[
                        currentAiSectionId ?? ""
                      ]?.finalContent.trim(),
                    );
                  const isEditing =
                    editingSectionId === currentAiSectionId && isSelected;
                  const preview =
                    option.content.length > 160 && !isExpanded
                      ? `${option.content.slice(0, 160)}...`
                      : option.content;
                  return (
                    <div
                      key={optionKey}
                      className="rounded border border-[var(--border)] bg-card px-3 py-3"
                    >
                      <div className="flex items-center gap-2">
                        <p className="text-xs font-bold text-primary">
                          {option.label}
                        </p>
                        <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold text-emerald-700">
                          {option.confidence}% match
                        </span>
                        <span className="rounded-full bg-violet-700 px-2 py-0.5 text-[10px] font-bold text-white">
                          AI
                        </span>
                      </div>
                      <p className="mt-2 whitespace-pre-wrap text-[11px] leading-4 text-foreground">
                        {preview}
                      </p>
                      {isEditing ? (
                        <div className="mt-3 space-y-2">
                          <textarea
                            className="min-h-[96px] w-full rounded border border-input bg-background px-2 py-2 text-[11px]"
                            value={editDraft}
                            onChange={(event) =>
                              setEditDraft(event.target.value)
                            }
                          />
                          <div className="flex gap-2">
                            <button
                              type="button"
                              className="rounded bg-violet-700 px-2 py-1 text-[11px] font-bold text-white"
                              onClick={() =>
                                currentAiSectionId &&
                                saveAiEdit(currentAiSectionId, optionIndex)
                              }
                            >
                              Save edit
                            </button>
                            <button
                              type="button"
                              className="rounded border px-2 py-1 text-[11px] font-bold"
                              onClick={() => setEditingSectionId(null)}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : null}
                      <div className="mt-3 flex items-center gap-2">
                        <button
                          type="button"
                          className="rounded border border-primary px-2 py-1 text-[11px] font-bold text-primary"
                          onClick={() =>
                            setExpandedOptionKey(isExpanded ? null : optionKey)
                          }
                        >
                          {isExpanded ? "Hide" : "View"}
                        </button>
                        <button
                          type="button"
                          className="rounded border border-violet-700 px-2 py-1 text-[11px] font-bold text-violet-700"
                          onClick={() =>
                            currentAiSectionId &&
                            startEditingAiOption(
                              currentAiSectionId,
                              optionIndex,
                              aiSelectionsBySection[currentAiSectionId]
                                ?.finalContent || option.content,
                            )
                          }
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className={`ml-auto rounded border px-2 py-1 text-[11px] font-bold ${
                            isSelected
                              ? "border-emerald-600 bg-emerald-50 text-emerald-700"
                              : "border-violet-700 text-violet-700"
                          }`}
                          onClick={() =>
                            currentAiSectionId &&
                            selectAiOption(
                              currentAiSectionId,
                              optionIndex,
                              option.content,
                              option.blocks,
                            )
                          }
                        >
                          {isSelected ? "Selected" : "Select"}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          {!allSectionsHaveAiSelection(
            selectedSectionIds,
            aiSelectionsBySection,
          ) ? (
            <div className="rounded border border-amber-500 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              View content or select one of 3 AI suggestions for each section —
              or edit before selecting
            </div>
          ) : null}
        </>
      )}
    </section>
  );

  const stepFive = (
    <section className="space-y-4 pt-3">
      <div className="rounded border border-[var(--border)] bg-card px-4 py-3">
        <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground">
          Section Order
        </p>
        <p className="mt-2 text-[11px] leading-4 text-muted-foreground">
          Use up/down controls to reorder sections. Selected AI content from
          Step 4 is shown below each section name.
        </p>
      </div>

      <div className="space-y-2">
        {isStepFiveMetadataPending ? (
          <div className="rounded border border-[var(--border)] bg-card px-3 py-4 text-center text-xs text-muted-foreground">
            <Loader2 className="mx-auto mb-2 h-4 w-4 animate-spin" />
            Loading section details...
          </div>
        ) : (
          orderedSections.map((id, index) => {
            const section = sectionById(id);
            const aiPreview = aiSelectionsBySection[id]?.finalContent;
            return (
              <div
                key={id}
                className="flex items-start gap-2 rounded border border-[var(--border)] bg-card px-3 py-2"
              >
                <span className="mt-0.5 w-4 text-center text-xs font-bold text-violet-700">
                  {index + 1}
                </span>
                <GripVertical className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground/50" />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-xs font-bold text-primary">
                    {section.name}
                  </span>
                  <span className="block truncate text-[11px] text-muted-foreground">
                    {section.description}
                  </span>
                  {aiPreview ? (
                    <span className="mt-1 block text-[10px] leading-4 text-muted-foreground/90">
                      {truncateText(aiPreview)}
                    </span>
                  ) : null}
                </span>
                <span className="flex shrink-0 flex-col gap-1">
                  <button
                    type="button"
                    className="rounded border px-2 py-0.5 text-[10px]"
                    disabled={index === 0}
                    onClick={() => moveSection(index, -1)}
                  >
                    ▲
                  </button>
                  <button
                    type="button"
                    className="rounded border px-2 py-0.5 text-[10px]"
                    disabled={index >= orderedSections.length - 1}
                    onClick={() => moveSection(index, 1)}
                  >
                    ▼
                  </button>
                </span>
              </div>
            );
          })
        )}
      </div>

      <div className="space-y-2">
        <div className="rounded border border-sky-200 bg-sky-50 px-3 py-2 text-[11px] leading-4 text-sky-900">
          Preview assembles a temporary document in Word using the section order
          below. Nothing is stored until you click Generate Document.
        </div>
        {documentPreviewError ? (
          <div className="rounded border border-destructive/40 bg-destructive/10 px-3 py-2 text-[11px] text-destructive">
            {documentPreviewError}
          </div>
        ) : null}
        <button
          type="button"
          className="w-full rounded border border-primary bg-background px-3 py-2 text-xs font-bold text-primary disabled:opacity-50"
          disabled={
            documentPreviewLoading ||
            orderedSections.length === 0 ||
            isSaving ||
            sessionHydrating
          }
          onClick={handleStepFivePreview}
        >
          {documentPreviewLoading
            ? "Updating preview in Word…"
            : "Preview in Word"}
        </button>
      </div>

      <div className="space-y-3">
        <div className="border-b border-violet-200 pb-1 text-[11px] font-bold uppercase tracking-wide text-violet-700">
          Document & Review
        </div>
        <label
          className="block text-xs font-bold text-foreground"
          htmlFor="generate-file-name"
        >
          Document File Name <span className="text-destructive">*</span>
          <input
            id="generate-file-name"
            type="text"
            className="mt-1 w-full rounded border border-input bg-background px-3 py-2 text-xs font-normal"
            placeholder="e.g. Summer-Sip-2026.docx"
            value={fileName}
            onChange={(event) => setFileName(event.target.value)}
          />
        </label>
        <p className="-mt-2 text-[11px] text-muted-foreground">
          Used as the saved file name in SharePoint (`.docx` added if omitted)
        </p>
        <label
          className="block text-xs font-bold text-foreground"
          htmlFor="generate-reviewer"
        >
          Assign Reviewer <span className="text-destructive">*</span>
          <select
            id="generate-reviewer"
            className="mt-1 w-full rounded border border-input bg-background px-3 py-2 text-xs font-normal"
            value={formValues.reviewerUserId}
            disabled={reviewersLoading}
            onChange={(event) =>
              setFormValues((prev) => ({
                ...prev,
                reviewerUserId: event.target.value,
              }))
            }
          >
            <option value="">
              {reviewersLoading ? "Loading reviewers..." : "Select reviewer..."}
            </option>
            {reviewers.map((user) => (
              <option
                key={user.id}
                value={user.id}
                disabled={!user.email?.trim()}
              >
                {user.name}
                {user.email?.trim() ? "" : " (no email)"}
              </option>
            ))}
          </select>
        </label>
        {reviewersError ? (
          <p className="-mt-2 text-[11px] text-destructive">{reviewersError}</p>
        ) : (
          <p className="-mt-2 text-[11px] text-muted-foreground">
            Document will be sent to the selected reviewer after generation
          </p>
        )}
        <label
          className="block text-xs font-bold text-foreground"
          htmlFor="generate-notes"
        >
          Notes
          <textarea
            id="generate-notes"
            className="mt-1 min-h-[74px] w-full rounded border border-input bg-background px-3 py-2 text-xs font-normal"
            placeholder="Optional notes for the reviewer..."
            value={formValues.notes}
            onChange={(event) =>
              setFormValues((prev) => ({ ...prev, notes: event.target.value }))
            }
          />
        </label>
      </div>

      <div className="space-y-3 rounded border border-[var(--border)] bg-card px-3 py-3">
        <label className="flex items-start gap-2">
          <input
            type="checkbox"
            className="mt-0.5 accent-violet-700"
            checked={saveAsTemplate}
            onChange={(event) => {
              setSaveAsTemplate(event.target.checked);
              if (event.target.checked && !saveTemplateName.trim()) {
                setSaveTemplateName(gameRuleName.trim());
              }
            }}
          />
          <span className="text-xs">
            <span className="block font-bold text-foreground">
              Save as template
            </span>
            <span className="mt-0.5 block text-[11px] font-normal leading-4 text-muted-foreground">
              Save this section package for reuse. Same name creates a new
              version.
            </span>
          </span>
        </label>
        {saveAsTemplate ? (
          <label
            className="block text-xs font-bold text-foreground"
            htmlFor="save-template-name"
          >
            Template name
            <input
              id="save-template-name"
              className="mt-1 w-full rounded border border-input bg-background px-3 py-2 text-xs font-normal"
              placeholder="e.g. Standard sweepstakes package"
              value={saveTemplateName}
              onChange={(event) => setSaveTemplateName(event.target.value)}
            />
          </label>
        ) : null}
      </div>
    </section>
  );

  const renderStep = () => {
    if (step === 1) return stepOne;
    if (step === 2) return stepTwo;
    if (step === 3) return stepThree;
    if (step === 4) return stepFour;
    return stepFive;
  };

  return (
    <div className="min-h-[calc(100vh-96px)] pb-16">
      {documentPreviewLoading ? (
        <div
          className="fixed inset-0 z-[300] flex items-center justify-center bg-background/85 backdrop-blur-[2px]"
          role="status"
          aria-live="polite"
          aria-busy="true"
        >
          <div className="flex flex-col items-center gap-3 rounded-lg border border-[var(--border)] bg-card px-8 py-6 shadow-lg">
            <Loader2
              className="h-9 w-9 animate-spin text-primary"
              aria-hidden
            />
            <p className="text-sm font-medium text-foreground">
              Updating preview in Word…
            </p>
          </div>
        </div>
      ) : null}
      {sessionHydrating || templateBootstrapLoading ? (
        <div
          className="fixed inset-0 z-[300] flex items-center justify-center bg-background/85 backdrop-blur-[2px]"
          role="status"
          aria-live="polite"
          aria-busy="true"
        >
          <div className="flex flex-col items-center gap-3 rounded-lg border border-[var(--border)] bg-card px-8 py-6 shadow-lg">
            <Loader2
              className="h-9 w-9 animate-spin text-primary"
              aria-hidden
            />
            <p className="text-sm font-medium text-foreground">
              {templateBootstrapLoading
                ? "Loading template…"
                : "Restoring your draft…"}
            </p>
          </div>
        </div>
      ) : null}
      <KbSectionClausePreviewModal
        open={clausePreview !== null}
        sectionName={clausePreview?.sectionName ?? ""}
        tag={clausePreview?.tag}
        description={clausePreview?.description}
        content={clausePreview?.content ?? ""}
        onClose={() => setClausePreview(null)}
      />
      {progressHeader}
      {error ? (
        <div className="mt-3 rounded border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
          {error}
        </div>
      ) : null}
      {success ? (
        <div className="mt-3 rounded border border-emerald-300 bg-emerald-50 px-3 py-2 text-xs text-emerald-800">
          {success}
        </div>
      ) : null}
      {renderStep()}
      <div className="fixed bottom-0 left-0 right-0 z-20 border-t border-[var(--border)] bg-background px-3 py-2">
        <div className="flex gap-2">
          <button
            type="button"
            className="w-[68px] rounded border border-input bg-background px-3 py-2 text-xs font-medium text-foreground disabled:opacity-50"
            onClick={goBack}
            disabled={sessionHydrating || isSaving || templateBootstrapLoading}
          >
            ← Back
          </button>
          <button
            type="button"
            className={`min-w-0 flex-1 rounded-md px-4 py-2 text-sm font-bold text-white disabled:cursor-not-allowed disabled:opacity-50 ${
              step === 5 ? "bg-emerald-600" : "bg-violet-700"
            }`}
            disabled={
              !canProceedToNextStep ||
              isSaving ||
              sessionHydrating ||
              templateBootstrapLoading
            }
            onClick={goNext}
          >
            {isSaving
              ? step === 5
                ? "Generating…"
                : "Saving…"
              : `${currentCopy.cta} →`}
          </button>
        </div>
      </div>
    </div>
  );
};
